import { useState, useRef, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Button, Flex, Input, Modal, Popover, Spin } from "antd"
import {
  FileOutlined,
  DeleteOutlined,
  MoreOutlined,
  PaperClipOutlined,
  CaretRightOutlined,
  DownloadOutlined,
  EyeOutlined
} from "@ant-design/icons"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"
import { Text } from "ui/Typography"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import CustomDrawer from "ui/Drawer"
import CollapsibleSection from "ui/CollapsibleSection"
import { toaster } from "components/common/Toast"
import {
  NOTE_PARENTS,
  listExperienceNotes,
  createExperienceNote,
  updateExperienceNote,
  deleteExperienceNote,
  selectNoteMutationLoading
} from "store/upcomingExperiences"
import styles from "./NotesSection.module.css"
import { spacings } from "themes/variables"
import { MAX_VISIBLE_IMAGES, NOTE_COLLAPSE_CHARS, NOTE_ALLOWED_EXTENSIONS, NOTE_MAX_BYTES } from "./constants"

const extensionOf = (name) => name.split(".").pop().toLowerCase()

const fileType = (name) => {
  const ext = extensionOf(name)
  if (NOTE_ALLOWED_EXTENSIONS.image.includes(ext)) return "image"
  if (NOTE_ALLOWED_EXTENSIONS.video.includes(ext)) return "video"
  return "file"
}

// Reject unsupported types / oversized files client-side. Returns a localized error string, or
// null when valid — `t` is passed in because this runs outside a component.
const validateFile = (file, t) => {
  const ext = extensionOf(file.name)
  const isAllowed = Object.values(NOTE_ALLOWED_EXTENSIONS).some((list) => list.includes(ext))
  if (!isAllowed) return t("booking.notes.file_type_error", { name: file.name })
  const type = fileType(file.name)
  if (file.size > NOTE_MAX_BYTES[type]) {
    return t("booking.notes.file_size_error", { name: file.name, size: Math.round(NOTE_MAX_BYTES[type] / (1024 * 1024)) })
  }
  return null
}

const readFileAsDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.readAsDataURL(file)
  })

// Open an attachment in a new tab (view / preview)
const viewAttachment = (attachment) => {
  if (attachment.url || attachment.dataUrl) {
    window.open(attachment.url || attachment.dataUrl, "_blank", "noopener,noreferrer")
  }
}

// Save a blob to disk under the given filename
const saveBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = objectUrl
  link.download = filename || "download"
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(objectUrl)
}

// Force a real download (not an in-browser preview). The `download` attribute is
// ignored for cross-origin URLs, so fetch the file as a blob and save that.
const downloadAttachment = async (attachment) => {
  const href = attachment.url || attachment.dataUrl
  if (!href) return
  try {
    const response = await fetch(href)
    const blob = await response.blob()
    saveBlob(blob, attachment.name)
  } catch {
    // Fallback: open in a new tab if the fetch is blocked (e.g. CORS)
    window.open(href, "_blank", "noopener,noreferrer")
  }
}

const AttachmentThumbnail = ({ attachment }) => {
  const src = attachment.dataUrl || attachment.url
  if (attachment.type === "image" && src) {
    return <img src={src} alt={attachment.name} className={styles.thumbImage} />
  }
  if (attachment.type === "video" && src) {
    return <video src={src} className={styles.thumbImage} muted preload="metadata" />
  }
  return (
    <div className={styles.thumbFile}>
      <FileOutlined className={styles.thumbFileIcon} />
    </div>
  )
}

AttachmentThumbnail.propTypes = {
  attachment: PropTypes.shape({
    type: PropTypes.string.isRequired,
    dataUrl: PropTypes.string,
    url: PropTypes.string,
    name: PropTypes.string.isRequired
  }).isRequired
}

// A document attachment row (PDF/doc/xls). View + download are always rendered: a note with a
// single file and short text has no "Show more", so gating the actions behind the expanded list
// left them permanently unreachable.
const AttachmentFileRow = ({ file, flex }) => {
  const { t } = useTranslation()
  return (
    <Flex align="center" flex={flex} className={styles.fileRow}>
      <FileOutlined className={styles.fileRowIcon} />
      <span className={styles.fileRowName}>{file.name}</span>
      <Flex align="center" gap={4} className={styles.fileRowActions}>
        <button type="button" className={styles.fileActionBtn} onClick={() => viewAttachment(file)} aria-label={t("common.view")}>
          <EyeOutlined />
        </button>
        <button
          type="button"
          className={styles.fileActionBtn}
          onClick={() => downloadAttachment(file)}
          aria-label={t("booking.notes.download")}
        >
          <DownloadOutlined />
        </button>
      </Flex>
    </Flex>
  )
}

AttachmentFileRow.propTypes = {
  file: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    dataUrl: PropTypes.string
  }).isRequired,
  flex: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

// Lightbox that plays a video / previews an image inside a modal
const MediaPreviewModal = ({ item, onClose }) => {
  const { t } = useTranslation()
  const src = item?.url || item?.dataUrl
  return (
    <Modal
      open={!!item}
      onCancel={onClose}
      title={item?.name}
      width={720}
      centered
      destroyOnClose
      footer={
        <Flex justify="flex-end">
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => downloadAttachment(item)}>
            {t("booking.notes.download")}
          </Button>
        </Flex>
      }
    >
      {item?.type === "video" ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={src} className={styles.previewMedia} controls autoPlay />
      ) : (
        <img src={src} alt={item?.name} className={styles.previewMedia} />
      )}
    </Modal>
  )
}

MediaPreviewModal.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    url: PropTypes.string,
    dataUrl: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired
}

const NoteCard = ({ note, onEdit, onDelete }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [previewItem, setPreviewItem] = useState(null)

  // Images and videos share the thumbnail grid; everything else is a file row.
  const media = note.attachments.filter((a) => a.type === "image" || a.type === "video")
  const files = note.attachments.filter((a) => a.type !== "image" && a.type !== "video")
  const totalAttachments = note.attachments.length

  const visibleMedia = media.slice(0, MAX_VISIBLE_IMAGES)
  const hiddenMediaCount = media.length - MAX_VISIBLE_IMAGES
  const displayMedia = isExpanded ? media : visibleMedia

  const firstFile = files[0] ?? null
  const hiddenFileCount = files.length - 1

  const isLong = note.text.length > NOTE_COLLAPSE_CHARS
  const displayText = isLong && !isExpanded ? note.text.slice(0, NOTE_COLLAPSE_CHARS) + "…" : note.text

  // "Show more/less" reveals the full text and every attachment (all media + all files).
  const canToggle = isLong || hiddenMediaCount > 0 || files.length > 1

  const menuContent = (
    <div className={styles.menuList}>
      <button
        type="button"
        className={styles.menuItem}
        onClick={() => {
          setIsMenuOpen(false)
          onEdit(note)
        }}
      >
        {t("booking.notes.edit_note")}
      </button>
      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuItemDelete}`}
        onClick={() => {
          setIsMenuOpen(false)
          onDelete(note)
        }}
      >
        {t("booking.notes.delete_note")}
      </button>
    </div>
  )

  return (
    <div className={styles.noteCard}>
      <Flex align="flex-start" justify="space-between" gap={4}>
        <Flex gap={spacings.XS} align="center">
          <span className={styles.noteAuthor}>{note.author}</span>
          <span className={styles.noteDate}>{note.date}</span>
        </Flex>
        {isMobile ? (
          <button type="button" className={styles.moreBtn} onClick={() => setIsMenuOpen(true)}>
            <MoreOutlined />
          </button>
        ) : (
          <Popover content={menuContent} trigger="click" placement="bottomRight" open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <button type="button" className={styles.moreBtn}>
              <MoreOutlined />
            </button>
          </Popover>
        )}
      </Flex>

      {/* Mobile: note actions in a bottom-sheet drawer instead of the popover */}
      {isMobile && (
        <CustomDrawer open={isMenuOpen} setOpen={setIsMenuOpen} title={t("global.actions")}>
          <Flex vertical gap={12} className={styles.actionsSheet}>
            <Button
              block
              shape="round"
              color="primary"
              variant="outlined"
              onClick={() => {
                setIsMenuOpen(false)
                onEdit(note)
              }}
            >
              {t("booking.notes.edit_note")}
            </Button>
            <Button
              block
              shape="round"
              danger
              onClick={() => {
                setIsMenuOpen(false)
                onDelete(note)
              }}
            >
              {t("booking.notes.delete_note")}
            </Button>
          </Flex>
        </CustomDrawer>
      )}

      <p className={styles.noteText}>{displayText}</p>

      {media.length > 0 && (
        <Flex align="center" gap={4} className={`${styles.imageRow} ${isExpanded ? styles.imageRowWrap : ""}`}>
          {/* Clicking a thumbnail opens the preview (view). Expanded tiles additionally
              expose a direct download icon; the preview modal also offers download. */}
          {displayMedia.map((item) =>
            isExpanded ? (
              <div key={item.id} className={`${styles.imageWrap} ${styles.imageWrapLarge}`}>
                <AttachmentThumbnail attachment={item} />
                <button
                  type="button"
                  className={styles.mediaViewArea}
                  onClick={() => setPreviewItem(item)}
                  aria-label={item.type === "video" ? t("booking.notes.play") : t("common.view")}
                >
                  {item.type === "video" && (
                    <span className={`${styles.videoBadge} ${styles.videoBadgeLarge}`}>
                      <CaretRightOutlined />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`${styles.mediaCircleBtn} ${styles.mediaDownloadBtn}`}
                  onClick={() => downloadAttachment(item)}
                  aria-label={t("booking.notes.download")}
                >
                  <DownloadOutlined />
                </button>
              </div>
            ) : (
              <button
                key={item.id}
                type="button"
                className={styles.imageWrap}
                onClick={() => setPreviewItem(item)}
                aria-label={item.type === "video" ? t("booking.notes.play") : t("common.view")}
              >
                <AttachmentThumbnail attachment={item} />
                {item.type === "video" && (
                  <span className={styles.videoBadge}>
                    <CaretRightOutlined />
                  </span>
                )}
              </button>
            )
          )}
          {!isExpanded && hiddenMediaCount > 0 && (
            <Flex justify="center" align="center" className={styles.moreImagesBadge}>
              +{hiddenMediaCount}
            </Flex>
          )}
        </Flex>
      )}

      {files.length > 0 &&
        (isExpanded ? (
          <Flex vertical gap={4} className={styles.fileList}>
            {files.map((file) => (
              <AttachmentFileRow key={file.id} file={file} />
            ))}
          </Flex>
        ) : (
          firstFile && (
            <Flex align="center" gap={4}>
              <AttachmentFileRow file={firstFile} flex={4} />
              {hiddenFileCount > 0 && (
                <Flex flex={1} align="center" justify="center" className={styles.moreFilesBadge}>
                  +{hiddenFileCount}
                </Flex>
              )}
            </Flex>
          )
        ))}

      {(canToggle || totalAttachments > 0) && (
        <Flex align="center" gap={8} className={styles.noteFooter}>
          {canToggle && (
            <button type="button" className={styles.toggleTextBtn} onClick={() => setIsExpanded((v) => !v)}>
              {isExpanded ? `∧ ${t("global.show_less")}` : `∨ ${t("global.show_more")}`}
            </button>
          )}
          {totalAttachments > 0 && (
            <span className={styles.attachmentCount}>
              <PaperClipOutlined className={styles.attachmentCountIcon} />
              {t("booking.notes.attachment_count", { total: totalAttachments })}
            </span>
          )}
        </Flex>
      )}

      <MediaPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </div>
  )
}

NoteCard.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    author: PropTypes.string,
    date: PropTypes.string,
    text: PropTypes.string,
    attachments: PropTypes.array.isRequired
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}

const AddNoteModal = ({ open, initial, onClose, onSave, saving = false, uploadPercent = null }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [text, setText] = useState("")
  const [attachments, setAttachments] = useState([])
  const [uploadingName, setUploadingName] = useState(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (open) {
      setText(initial?.text ?? "")
      setAttachments(initial?.attachments ?? [])
      setUploadingName(null)
    }
  }, [open, initial])

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList)
    for (const file of files) {
      const error = validateFile(file, t)
      if (error) {
        toaster.error(error)
        continue
      }
      const type = fileType(file.name)
      const tempId = `${Date.now()}_${file.name}`
      setUploadingName(file.name)
      const dataUrl = type === "image" ? await readFileAsDataUrl(file) : null
      setAttachments((prev) => [...prev, { id: tempId, name: file.name, type, dataUrl, file }])
      setUploadingName(null)
    }
  }, [t])

  const handleRemoveAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  // `initial` is only set when editing an existing note, so it also drives the title.
  const modalTitle = initial ? t("booking.notes.edit_note") : t("booking.notes.modal_title")

  const handleSave = () => {
    if (!text.trim()) return
    // Existing (already-uploaded) attachments the user removed → tell the backend to delete them.
    const remainingIds = new Set(attachments.filter((att) => att.uploaded).map((att) => att.id))
    const removedAttachmentIds = (initial?.attachments ?? [])
      .filter((att) => att.uploaded && !remainingIds.has(att.id))
      .map((att) => att.id)
    onSave({ text: text.trim(), attachments, removedAttachmentIds })
  }

  const body = (
    <Flex vertical gap={12}>
      <Flex vertical gap={4}>
        <span className={styles.modalLabel}>{t("booking.notes.field_label")}</span>
        <Input.TextArea
          placeholder={t("booking.notes.placeholder")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
        />
      </Flex>

      <Flex vertical gap={8}>
        <span className={styles.modalLabel}>{t("booking.notes.attach_label")}</span>
        <Button
          icon={<PaperClipOutlined />}
          onClick={() => fileInputRef.current?.click()}
          color="primary"
          variant="outlined"
          shape="round"
          className={styles.attachBtn}
        >
          {t("booking.notes.attach_files")}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
          className={styles.hiddenInput}
          onChange={(e) => {
            handleFiles(e.target.files)
            e.target.value = ""
          }}
        />

        {(attachments.length > 0 || uploadingName) && (
          <Flex vertical gap={12} className={styles.modalFileList}>
            {attachments.map((att) => (
              <Flex key={att.id} align="center" gap={12} className={styles.modalFileRow}>
                <FileOutlined className={styles.modalFileIcon} />
                <span className={styles.fileRowName}>{att.name}</span>
                <button type="button" className={styles.fileRemoveBtn} onClick={() => handleRemoveAttachment(att.id)}>
                  <DeleteOutlined />
                </button>
              </Flex>
            ))}

            {/* Reading a picked file into a data URL — near-instant and not cancellable, so it
                gets a spinner rather than a progress bar. */}
            {uploadingName && (
              <Flex align="center" gap={12} className={styles.modalFileRow}>
                <FileOutlined className={styles.modalFileIcon} />
                <span className={styles.fileRowName}>{t("booking.notes.uploading", { name: uploadingName })}</span>
                <Spin size="small" />
              </Flex>
            )}
          </Flex>
        )}

        {/* Real upload progress, reported by axios while the multipart save is in flight. */}
        {uploadPercent != null && (
          <Flex align="center" gap={12} className={styles.uploadInfo}>
            <div className={styles.uploadProgressTrack}>
              <div className={styles.uploadProgressBar} style={{ width: `${uploadPercent}%` }} />
            </div>
            <span className={styles.uploadProgressLabel}>{t("booking.notes.upload_percent", { percent: uploadPercent })}</span>
          </Flex>
        )}
      </Flex>
    </Flex>
  )

  const cancelBtn = (
    <Button onClick={onClose} disabled={saving}>{t("global.cancel")}</Button>
  )
  const saveBtn = (
    <Button type="primary" onClick={handleSave} disabled={!text.trim() || saving} loading={saving}>
      {t("global.save")}
    </Button>
  )

  // Mobile: render as a bottom-sheet drawer with a full-width footer
  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={(next) => !next && onClose()} title={modalTitle}>
        <Flex vertical gap={16} className={styles.addNoteDrawerBody}>
          {body}
          <Flex gap={8} className={styles.addNoteDrawerFooter}>
            <Button block shape="round" onClick={onClose} disabled={saving}>{t("global.cancel")}</Button>
            <Button block shape="round" type="primary" onClick={handleSave} disabled={!text.trim() || saving} loading={saving}>
              {t("global.save")}
            </Button>
          </Flex>
        </Flex>
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      title={modalTitle}
      onCancel={onClose}
      footer={
        <Flex gap={8} justify="flex-start">
          {cancelBtn}
          {saveBtn}
        </Flex>
      }
      width={480}
      destroyOnClose
    >
      {body}
    </Modal>
  )
}

AddNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.shape({
    text: PropTypes.string,
    attachments: PropTypes.array
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  uploadPercent: PropTypes.number
}

const DeleteNoteModal = ({ open, onClose, onConfirm, deleting = false }) => {
  const { t } = useTranslation()
  return (
    <Modal open={open} onCancel={onClose} footer={null} width={400} closable centered>
      <Flex vertical align="flex-start" gap={12} className={styles.deleteModalBody}>
        <div className={styles.deleteIcon}>
          <AlertIcon width={24} height={24} />
        </div>
        <Flex vertical gap={4}>
          <span className={styles.deleteTitle}>{t("booking.notes.delete_note")}</span>
          <span className={styles.deleteSubtitle}>{t("booking.notes.delete_confirm")}</span>
        </Flex>
        <Flex gap={8} className={styles.deleteModalActions}>
          <Button onClick={onClose} disabled={deleting}>{t("global.cancel")}</Button>
          <Button danger type="primary" onClick={onConfirm} loading={deleting}>
            {t("global.delete")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

DeleteNoteModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deleting: PropTypes.bool
}

// Backend attachment ({ id, filename, content_type, url }) → display shape
// Backend `kind` is "image" | "video" | "document"; map to our renderer's "image"/"video"/"file".
const mapAttachment = (att) => {
  const kind = att.kind || (att.content_type || "").split("/")[0]
  const type = kind === "image" ? "image" : kind === "video" ? "video" : "file"
  return {
    id: att.id,
    name: att.filename || att.name || "",
    type,
    dataUrl: type === "image" ? att.url : null,
    url: att.url || null,
    uploaded: true
  }
}

// Backend note ({ id, content, created_at, author: { name, type }, attachments }) → display shape
const mapNote = (note) => ({
  id: note.id,
  author: note.author?.name || "",
  authorType: note.author?.type || "",
  date: note.created_at ? new Date(note.created_at).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "",
  text: note.content ?? "",
  attachments: Array.isArray(note.attachments) ? note.attachments.map(mapAttachment) : []
})

// The mutation response may return the note at the body root, under `data`, or under `note`.
// Return the mapped note when present; otherwise null so the caller can fall back locally.
const noteFromResponse = (res) => {
  const note = res?.data ?? res?.note ?? (res?.id != null ? res : null)
  return note?.id != null ? mapNote(note) : null
}

const NotesSection = ({
  bookingId = "default",
  notes: initialNotes = [],
  hideTitle = false,
  parent = NOTE_PARENTS.upcoming,
  collapsible = false,
  onCountChange
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const mutationLoading = useSelector(selectNoteMutationLoading)
  const [notes, setNotes] = useState(() => initialNotes.map(mapNote))
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editNote, setEditNote] = useState(null)
  const [deleteNote, setDeleteNote] = useState(null)
  // 0–100 while a multipart save is uploading; null when no upload is in flight.
  const [uploadPercent, setUploadPercent] = useState(null)

  // Always load the rich notes from the notes endpoint. The embedded detail payload carries a
  // thin shape (no author, no attachments) — reservations dropped it entirely, and Upcoming's is
  // too thin to manage attachments — so `GET {P}/notes` is the source of truth. `initialNotes`
  // just seeds the first paint to avoid a flash.
  useEffect(() => {
    if (!bookingId || bookingId === "default") return
    let active = true
    dispatch(listExperienceNotes(bookingId, parent))
      .then((res) => {
        if (active && Array.isArray(res?.data)) setNotes(res.data.map(mapNote))
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [dispatch, bookingId, parent])

  // Report the live count up so a collapsed parent header stays in sync after add/edit/delete.
  useEffect(() => {
    onCountChange?.(notes.length)
  }, [notes.length, onCountChange])

  const handleSave = async ({ text, attachments, removedAttachmentIds = [] }) => {
    // PATCH/POST append new files; existing (already-uploaded) attachments carry no File.
    const files = attachments.filter((att) => att.file).map((att) => att.file)
    // Axios reports `total` only when the browser can compute it; without it there is no
    // percentage to show, so the bar stays hidden rather than guessing.
    const handleUploadProgress = files.length
      ? (event) => {
          if (!event.total) return
          setUploadPercent(Math.min(100, Math.round((event.loaded * 100) / event.total)))
        }
      : undefined
    if (files.length) setUploadPercent(0)
    try {
      if (editNote) {
        const res = await dispatch(
          updateExperienceNote(
            bookingId,
            editNote.id,
            { content: text, files, removeAttachmentIds: removedAttachmentIds },
            parent,
            handleUploadProgress
          )
        )
        // Prefer the server note; if the response omits it, reflect the edit locally so the
        // save is never silently dropped from the UI.
        const updated = noteFromResponse(res) ?? { ...editNote, text, attachments }
        setNotes((prev) => prev.map((note) => (note.id === editNote.id ? updated : note)))
        setEditNote(null)
      } else {
        const res = await dispatch(createExperienceNote(bookingId, { content: text, files }, parent, handleUploadProgress))
        const created = noteFromResponse(res)
        if (created) setNotes((prev) => [created, ...prev])
      }
      setIsAddOpen(false)
    } catch {
      // success toast + error toast are handled by the store/api helper
    } finally {
      setUploadPercent(null)
    }
  }

  const handleEdit = (note) => {
    setEditNote(note)
    setIsAddOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteExperienceNote(bookingId, deleteNote.id, parent))
      setNotes((prev) => prev.filter((note) => note.id !== deleteNote.id))
      setDeleteNote(null)
    } catch {
      // success toast + error toast are handled by the store/api helper
    }
  }

  const addNoteButton = (
    <Button
      color="primary"
      variant="outlined"
      size="small"
      className={styles.addNoteBtn}
      onClick={() => {
        setEditNote(null)
        setIsAddOpen(true)
      }}
    >
      {t("booking.notes.add_note")}
    </Button>
  )

  const notesList =
    notes.length === 0 ? (
      <Text className={styles.notesEmpty}>{t("booking.notes.empty")}</Text>
    ) : (
      <Flex vertical gap={0}>
        {notes.map((note) => (
          <NoteCard key={note.id} note={note} onEdit={handleEdit} onDelete={setDeleteNote} />
        ))}
      </Flex>
    )

  return (
    <>
      {collapsible ? (
        <CollapsibleSection title={t("booking.notes.title")} count={notes.length}>
          <Flex justify="flex-end" className={styles.notesHeader}>{addNoteButton}</Flex>
          {notesList}
        </CollapsibleSection>
      ) : (
        <>
          <Flex align="center" justify={hideTitle ? "flex-end" : "space-between"} className={styles.notesHeader}>
            {!hideTitle && <Text className={styles.sectionTitle}>{t("booking.notes.title")}</Text>}
            {addNoteButton}
          </Flex>
          {notesList}
        </>
      )}

      <AddNoteModal
        open={isAddOpen}
        initial={editNote}
        saving={mutationLoading}
        uploadPercent={uploadPercent}
        onClose={() => {
          setIsAddOpen(false)
          setEditNote(null)
        }}
        onSave={handleSave}
      />

      <DeleteNoteModal open={!!deleteNote} deleting={mutationLoading} onClose={() => setDeleteNote(null)} onConfirm={handleDeleteConfirm} />
    </>
  )
}

NotesSection.propTypes = {
  bookingId: PropTypes.string,
  notes: PropTypes.array,
  hideTitle: PropTypes.bool,
  parent: PropTypes.oneOf([NOTE_PARENTS.upcoming, NOTE_PARENTS.reservation]),
  collapsible: PropTypes.bool,
  onCountChange: PropTypes.func
}

export default NotesSection
